
import cookies from 'js-cookie';
import { defineComponent, getCurrentInstance, onMounted, ref, onUnmounted, watch } from 'vue';
import Toast from 'vant/lib/toast';
import { formatUrls, commonPvParams, copyToClipboard } from '@/common/utils';
import VideoAction from '@/components/video-swiper/components/video-action.vue';
import VideoInfo from '@/components/video-swiper/components/video-info.vue';
import NewOpenKwaiNotice from '@/components/new-open-kwai-notice/index.vue';
import { sendLog, immediateSendLog, getKwaiLog, radarLog } from '@/common/radar';
import { getMetaInfo, header, extractTitleFromMetaInfo, getDefaultTitle, getLdJson } from '@/common/seo';
import { setCommonParamsCookiesInServer } from '@/common/common-params';
import CommentDialog from '@/components/comment-dialog/seo-index.vue';
import { useCommentDialog } from '@/components/comment-dialog/hooks/useCommentDialog';
import { Picture } from '~/api/topic';
import { PictureActionBarSeoSetup } from './scripts/seo';
import { PitureActionBarAppSetup } from './scripts/app';

interface HeadUrl {
    cdn: string; // Content Delivery Network domain
    url: string; // URL of the user's profile picture
}

export default defineComponent<any>({
    components: {
        VideoAction,
        VideoInfo,
        NewOpenKwaiNotice,
        CommentDialog,
    },
    layout(ctx) {
        return ctx.$pwaLayout || 'default';
    },
    setup() {
        const fmp_time = Date.now();
        const { proxy } = getCurrentInstance() as { proxy: Record<string, any> };

        const { commentLists, commentDialogVisable, showCommentLoading, fetchSeoComment } = useCommentDialog();

        const isNoPicture = ref(false); // 没有查询到对应video
        const pictureDetail = ref<Picture>();
        const setupConfig = proxy?.$isAppEnvironment ? PitureActionBarAppSetup() : PictureActionBarSeoSetup();

        // 双击 hooks 逻辑
        const videoClickCount = ref(0); // 单击双击
        let videoClickTimer = null as any;
        // 屏幕双击
        const checkDbClick = async (e: MouseEvent) => {
            videoClickCount.value++;
            if (videoClickCount.value === 1) {
                videoClickTimer = setTimeout(() => {
                    videoClickCount.value = 0;
                }, 300); // 设置一个时间间隔，单位为毫秒
            } else if (videoClickCount.value === 2) {
                clearTimeout(videoClickTimer);
                videoClickCount.value = 0;
                renderLiked(e);
                showNewOpenKwaiNotice.value = true;
            }
        };
        // 在点击位置渲染红心
        const renderLiked = (event: MouseEvent) => {
            const heartWrap = document.createElement('div');
            const heartIcon = document.createElement('div');
            heartIcon.className = 'heart-icon';
            heartIcon.style.transform = `rotate(${Math.floor(Math.random() * 61) - 30}deg)`;
            heartWrap.className = 'heart-dbclick-warp';
            heartWrap.style.top = `${event.clientY}px`;
            heartWrap.style.left = `${event.clientX}px`;
            heartWrap.appendChild(heartIcon);
            document.body.appendChild(heartWrap);
            setTimeout(() => {
                document.body.removeChild(heartWrap);
            }, 300);
        };
        // 双击 hooks 逻辑结束

        // 点赞关注
        const handleActionClick = ({ type }: { type: string }) => {
            sendLog({
                type: 'click',
                name: 'DETAIL_BUTTON',
                value: {
                    photo_id: `${pictureDetail.value?.pic_id_str}`,
                    author_id: `${pictureDetail.value?.kwai_id}`,
                    button_type: type,
                },
            });
            if (type === 'download') {
                setupConfig.handleDownloadAction(pictureDetail.value);
            } else if (type === 'comment' && pictureDetail.value?.pic_id_str) {
                commentDialogVisable.value = true;
                fetchSeoComment(pictureDetail.value?.pic_id_str);
            } else if (type === 'share') {
                const copyUrl = `https://${window.location.host}/@${pictureDetail.value?.kwai_id}/picture/${pictureDetail.value?.pic_id_str}`;
                copyToClipboard(copyUrl);
                Toast({
                    message: proxy.$root.$t('k_446636'),
                });
            } else if (type === 'favorite') {
                setupConfig.handleLikeAction();
            } else {
                showNewOpenKwaiNotice.value = true;
            }
        };

        const handleInfoClick = async ({ type }: { type: string }) => {
            sendLog({
                type: 'click',
                name: 'DETAIL_BUTTON',
                value: {
                    picture_id: `${pictureDetail.value?.pic_id_str}`,
                    author_id: `${pictureDetail.value?.kwai_id}`,
                    button_type: type,
                },
            });

            if (type === 'name' || type === 'photo') {
                // 影子账号，不进入profile页,后续打开
                Toast({ message: proxy.$root.$t('picture_click_user_toast') });
                return;
            }

            if (type === 'follow') {
                // 影子账号，不让关注
                Toast({ message: proxy.$root.$t('not_allow_follow') });
                return;
            }

            showNewOpenKwaiNotice.value = true;
        };

        // popup
        const showNewOpenKwaiNotice = ref(false);
        const handleNewOpenKwaiNoticeClose = (params: Record<string, string>) => {
            sendLog({
                type: 'click',
                name: 'DETAIL_POP',
                value: {
                    photo_id: `${pictureDetail.value?.pic_id_str}`,
                    author_id: `${pictureDetail.value?.kwai_id}`,
                    button_type: 'wait',
                    pop_type: 'download_pop',
                },
            });
            showNewOpenKwaiNotice.value = false;
        };

        const handleNewOpenKwaiNotice = (params: Record<string, string>) => {
            proxy.$bus.emit('launchApp', {
                callAppPath: `work/${proxy.$route.params.photoId}`,
            });
            sendLog({
                type: 'click',
                name: 'DETAIL_POP',
                value: {
                    photo_id: `${pictureDetail.value?.pic_id_str}`,
                    author_id: `${pictureDetail.value?.kwai_id}`,
                    button_type: 'download',
                    pop_type: 'download_pop',
                },
            });
            setTimeout(() => {
                showNewOpenKwaiNotice.value = false;
            }, 0);
        };
        watch(
            () => showNewOpenKwaiNotice.value,
            newValue => {
                if (newValue) {
                    sendLog({
                        type: 'show',
                        name: 'DETAIL_POP',
                        value: {
                            photo_id: `${pictureDetail.value?.pic_id_str}`,
                            author_id: `${pictureDetail.value?.kwai_id}`,
                            button_type: 'download_pop',
                        },
                    });
                }
            }
        );

        const backClickHandle = () => {
            window.history.back();
            sendLog({
                type: 'click',
                name: 'DETAIL_BUTTON',
                value: {
                    photo_id: `${pictureDetail.value?.pic_id_str}`,
                    author_id: `${pictureDetail.value?.kwai_id}`,
                    button_type: 'back',
                },
            });
        };

        const sendPv = () => {
            const params = commonPvParams({ defaultPageSource: 'detail' });
            // 获取启动config
            const country = proxy.$store.state.startup.countryInfo;
            const user_id = cookies.get('user_id');
            const picture_id = pictureDetail.value?.pic_id_str;
            immediateSendLog({
                type: 'pv',
                name: 'MOBILE_SEO_DETAIL_PAGE',
                value: {
                    country,
                    user_id,
                    picture_id,
                    ...params,
                },
            });
        };

        onMounted(async () => {
            const webLogger = getKwaiLog();
            try {
                webLogger?.plugins?.radar?.fmp(fmp_time);
            } catch (error) {
                console.error(error);
            }

            if (proxy?.picture) {
                console.log('testzq-picture from async', proxy?.picture);
                pictureDetail.value = proxy.picture;
            } else if (proxy?.$store.state.clickedPicture.pic_id_str) {
                console.log('testzq-picture from state', proxy?.$store.state.clickedPicture);
                pictureDetail.value = proxy.$store.state.clickedPicture;
            } else {
                console.log('testzq-picture', 'no picture');
                isNoPicture.value = true;
            }

            sendPv();
            const posterUrl = pictureDetail.value?.pic_origin_url;
            console.log('onMounted111', posterUrl);
            console.log('testzq', JSON.stringify(pictureDetail.value));
        });

        return {
            proxy,
            pictureDetail,
            showNewOpenKwaiNotice,
            isNoPicture,
            commentLists,
            commentDialogVisable,
            showCommentLoading,
            fetchSeoComment,
            checkDbClick,
            handleActionClick,
            formatUrls,
            handleInfoClick,
            handleNewOpenKwaiNoticeClose,
            handleNewOpenKwaiNotice,
            backClickHandle,

            isLiked: setupConfig.isLiked,
        };
    },
    async asyncData(ctx) {
        let picture;
        let seoData = [];
        let metaInfoData = [];
        let title = getDefaultTitle(ctx);
        const { query } = ctx;

        if (process.server) {
            setCommonParamsCookiesInServer({
                bucket: ctx.store.state.startup.bucket,
                host: ctx.req.headers.host,
                ctx,
                countryInfo: ctx.store.state.startup.countryInfo,
            });

            try {
                const { photoId, share_device_id, shareBucket } = ctx.route?.query;
                const realPhotoId = ctx?.route?.params?.photoId || photoId;

                const url = `https://${ctx?.req?.headers?.host}${ctx?.route?.path}` || '';
                // 获取google ldjson 标签
                const seoPromise = getLdJson({ ctx, params: { url } });
                // 获取meta info
                const metaInfoPromise = getMetaInfo({ ctx, params: { url, locale: ctx.req.headers['accept-language'] } });

                const pictureDetailPromise = ctx.$axios.$$post('overseaAPI::/rest/o/w/pic/detail', {
                    picId: realPhotoId,
                });

                const [ppictureDetailRes, seoRes, metaInfoRes] = await Promise.all([pictureDetailPromise, seoPromise, metaInfoPromise]);

                if (ppictureDetailRes.result === 1) {
                    picture = ppictureDetailRes.data;
                }

                if (seoRes.status === 200) {
                    seoData = seoRes.data;
                }

                if (metaInfoRes.status === 200) {
                    metaInfoData = metaInfoRes.data;
                    title = extractTitleFromMetaInfo({ ctx, metaInfoData });
                }
            } catch (error) {
                console.log(error);
            }
        }

        return {
            picture,
            seoData,
            metaInfoData,
            title,
        };
    },

    head() {
        const head = header(this);
        return {
            ...head,
        };
    },
});
